import { useEffect, useRef } from 'react';

export default function useIntersectionObserver({
    observeSelector,
    beforeClass = 'is-before',
    afterClass = 'is-after',
    activeClass = 'is-active',
    scrollUpClass = 'is-scroll-up',
    scrollDownClass = 'is-scroll-down'
}) {
    const elementRef = useRef(null);
    const lastScrollYRef = useRef(window.scrollY);

    useEffect(() => {
        const element = elementRef.current;
        if (!element || !observeSelector) return;

        const targets = Array.from(document.querySelectorAll(observeSelector));
        if (!targets.length) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.boundingClientRect.bottom < 0) {
                    element.classList.remove(beforeClass, activeClass);
                    element.classList.add(afterClass);
                } else if (entry.boundingClientRect.top > window.innerHeight) {
                    element.classList.remove(afterClass, activeClass);
                    element.classList.add(beforeClass);
                } else {
                    element.classList.remove(beforeClass, afterClass);
                    element.classList.add(activeClass);
                }
            });
        });

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const isScrollingDown = currentScrollY > lastScrollYRef.current;
            const isScrollingUp = currentScrollY < lastScrollYRef.current;

            if (isScrollingDown) {
                element.classList.remove(scrollUpClass);
                element.classList.add(scrollDownClass);
            } else if (isScrollingUp) {
                element.classList.remove(scrollDownClass);
                element.classList.add(scrollUpClass);
            }

            lastScrollYRef.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll);
        targets.forEach((target) => observer.observe(target));

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, [observeSelector, beforeClass, afterClass, activeClass, scrollUpClass, scrollDownClass]);

    return elementRef;
}